import { Activity, AlertTriangle, CheckCircle2, Clock, Loader2, Mail, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useQueueHealth } from '@/hooks/useEmailBackend';

/**
 * Workspace Admin queue-health card (Task #24).
 *
 * Shows workspace-scoped queue/cron liveness:
 *   - Active campaigns
 *   - Pending in queue
 *   - Currently processing
 *   - Overdue (status GREEN if zero, YELLOW if any)
 *   - Sent in last 1 hour
 *   - Sent in last 24 hours
 *
 * System-wide processor liveness (last run, cron job active, etc.) is
 * intentionally NOT surfaced here — that's super-admin territory and lives
 * in QueueProcessorHealthCard inside the Infrastructure panel.
 *
 * Polls every 30s via useQueueHealth (matches useSafetyStatus cadence).
 */
export function QueueHealthCard() {
  const { data: health, isLoading, error } = useQueueHealth();

  const hasOverdue = (health?.ws_overdue_count ?? 0) > 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Workspace queue health
        </CardTitle>
        {!isLoading && !error && (
          hasOverdue ? (
            <Badge variant="default" className="bg-yellow-500 hover:bg-yellow-500">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Overdue jobs
            </Badge>
          ) : (
            <Badge variant="secondary" className="bg-green-100 text-green-700 hover:bg-green-100">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Healthy
            </Badge>
          )
        )}
      </CardHeader>
      <CardContent>
        {error ? (
          <div className="text-sm text-red-600">
            Failed to load queue health: {(error as Error).message}
          </div>
        ) : isLoading || !health ? (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Metric
              icon={<Mail className="h-4 w-4 text-muted-foreground" />}
              label="Active campaigns"
              value={health.ws_active_campaigns}
            />
            <Metric
              icon={<Clock className="h-4 w-4 text-yellow-500" />}
              label="Pending"
              value={health.ws_total_pending}
            />
            <Metric
              icon={<Loader2 className="h-4 w-4 text-blue-500" />}
              label="Processing"
              value={health.ws_processing_count}
            />
            <Metric
              icon={<AlertTriangle className={`h-4 w-4 ${hasOverdue ? 'text-yellow-600' : 'text-muted-foreground'}`} />}
              label="Overdue"
              value={health.ws_overdue_count}
              tone={hasOverdue ? 'warn' : 'default'}
            />
            <Metric
              icon={<Send className="h-4 w-4 text-green-600" />}
              label="Sent (1h)"
              value={health.ws_sent_1h}
            />
            <Metric
              icon={<Send className="h-4 w-4 text-green-600" />}
              label="Sent (24h)"
              value={health.ws_sent_24h}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({
  icon,
  label,
  value,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone?: 'default' | 'warn';
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div
        className={`text-2xl font-bold ${
          tone === 'warn' ? 'text-yellow-600' : ''
        }`}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}
